// ── Game constants and balance tuning ──
//
// Combat-specific constants (attack damage, attack cooldowns, projectile
// speeds, windup durations, combo thresholds, etc.) were removed in the
// Ritual Unburdening Phase B refactor. Movement, patrol, detection, and
// behavioral-phase constants are retained for AI repurposing in Phase C.
/** Game constants shared across client and server */
export const TILE_SIZE = 32;
export const PLAYER_SPEED = 160; // pixels per second (legacy — use WALK_SPEED / HURRY_SPEED)
// ── Two-pace movement (AGE-1137) ──
/** Default walking pace — trot-like, rhythmic (px/s) */
export const WALK_SPEED = 95;
/** Brisk walk when holding Shift — not a sprint (px/s) */
export const HURRY_SPEED = 135;
/** Seconds to ease from idle → walk speed */
export const MOVE_ACCEL_TIME = 0.1;
/** Seconds to ease from walk → idle */
export const MOVE_DECEL_TIME = 0.15;
/** Seconds to blend between walk ↔ hurry pace */
export const HURRY_BLEND_TIME = 0.2;
export const TICK_RATE = 20; // server ticks per second
export const WORLD_WIDTH = 2400;
export const WORLD_HEIGHT = 1800;
/** Player defaults */
export const PLAYER_MAX_HEALTH = 100;
export const PLAYER_MAX_MANA = 50;
/** Enemy defaults. */
export const ENEMY_DEFAULT_AGGRO_RANGE = 200; // pixels
export const ENEMY_DEFAULT_HEALTH = 500;
export const ENEMY_HALF_SIZE = (TILE_SIZE - 4) / 2; // hitbox half-size, same formula as player
/** Melee rusher defaults (data-driven, can be overridden per-instance) */
export const MELEE_RUSHER_HEALTH = 400;
export const MELEE_RUSHER_SPEED = 100; // pixels per second
export const MELEE_RUSHER_CHASE_SPEED = 140;
export const MELEE_RUSHER_AGGRO_RANGE = 200; // pixels — detection radius
export const MELEE_RUSHER_DEAGGRO_RANGE = 280;
export const MELEE_RUSHER_PATROL_RADIUS = 96;
export const MELEE_RUSHER_IDLE_DURATION_MS = 4000;
export const MELEE_RUSHER_PATROL_PAUSE_MS = 3000;
/** Ranged attacker defaults */
export const RANGED_ATTACKER_HEALTH = 300;
export const RANGED_ATTACKER_SPEED = 70;
export const RANGED_ATTACKER_CHASE_SPEED = 90;
export const RANGED_ATTACKER_AGGRO_RANGE = 250;
export const RANGED_ATTACKER_DEAGGRO_RANGE = 320;
export const RANGED_ATTACKER_MIN_RANGE = 80; // pixels — too close, triggers retreat
export const RANGED_ATTACKER_PATROL_RADIUS = 64;
export const RANGED_ATTACKER_IDLE_DURATION_MS = 5000;
export const RANGED_ATTACKER_PATROL_PAUSE_MS = 4000;
export const RANGED_ATTACKER_RETREAT_SPEED = 120;
/** Shielded tank defaults */
export const SHIELDED_TANK_HEALTH = 600;
export const SHIELDED_TANK_SPEED = 60;
export const SHIELDED_TANK_CHASE_SPEED = 60;
export const SHIELDED_TANK_AGGRO_RANGE = 150;
export const SHIELDED_TANK_DEAGGRO_RANGE = 220;
export const SHIELDED_TANK_PATROL_RADIUS = 64;
export const SHIELDED_TANK_IDLE_DURATION_MS = 5000;
export const SHIELDED_TANK_PATROL_PAUSE_MS = 4000;
export const SHIELDED_TANK_SHIELD_ARC = Math.PI; // 180° frontal arc (radians)
/** Teleporting caster defaults. */
export const TELEPORTING_CASTER_HEALTH = 200;
export const TELEPORTING_CASTER_SPEED = 80;
export const TELEPORTING_CASTER_CHASE_SPEED = 80;
export const TELEPORTING_CASTER_AGGRO_RANGE = 300;
export const TELEPORTING_CASTER_DEAGGRO_RANGE = 380;
export const TELEPORTING_CASTER_PATROL_RADIUS = 80;
export const TELEPORTING_CASTER_IDLE_DURATION_MS = 4000;
export const TELEPORTING_CASTER_PATROL_PAUSE_MS = 3000;
export const TELEPORTING_CASTER_TELEPORT_RANGE = 150;
export const TELEPORTING_CASTER_TELEPORT_TRIGGER = 100;
export const TELEPORTING_CASTER_TELEPORT_COOLDOWN_MS = 8000;
/** Dungeon boss defaults. */
export const DUNGEON_BOSS_HEALTH = 2000;
export const DUNGEON_BOSS_SPEED = 80;
export const DUNGEON_BOSS_CHASE_SPEED = 120;
export const DUNGEON_BOSS_AGGRO_RANGE = 400;
export const DUNGEON_BOSS_DEAGGRO_RANGE = 500;
export const DUNGEON_BOSS_PATROL_RADIUS = 128;
export const DUNGEON_BOSS_IDLE_DURATION_MS = 2000;
export const DUNGEON_BOSS_PATROL_PAUSE_MS = 1600;
export const DUNGEON_BOSS_PHASE2_THRESHOLD = 0.5;
export const DUNGEON_BOSS_PHASE2_SPEED_MULT = 1.3;
export const DUNGEON_BOSS_SUMMON_COOLDOWN_MS = 16000;
export const DUNGEON_BOSS_SUMMON_COUNT = 2;
/** Shadow Assassin defaults */
export const SHADOW_ASSASSIN_HEALTH = 250;
export const SHADOW_ASSASSIN_SPEED = 60;
export const SHADOW_ASSASSIN_CHASE_SPEED = 180;
export const SHADOW_ASSASSIN_AGGRO_RANGE = 220;
export const SHADOW_ASSASSIN_DEAGGRO_RANGE = 300;
export const SHADOW_ASSASSIN_PATROL_RADIUS = 80;
export const SHADOW_ASSASSIN_IDLE_DURATION_MS = 3000;
export const SHADOW_ASSASSIN_PATROL_PAUSE_MS = 2400;
export const SHADOW_ASSASSIN_STEALTH_SPEED = 60;
export const SHADOW_ASSASSIN_DASH_RANGE = 120;
export const SHADOW_ASSASSIN_STEALTH_ENTER_MS = 3000;
export const SHADOW_ASSASSIN_REVEAL_RANGE = 64;
/** Necromancer defaults. */
export const NECROMANCER_HEALTH = 350;
export const NECROMANCER_SPEED = 55;
export const NECROMANCER_CHASE_SPEED = 70;
export const NECROMANCER_AGGRO_RANGE = 280;
export const NECROMANCER_DEAGGRO_RANGE = 360;
export const NECROMANCER_MIN_RANGE = 100;
export const NECROMANCER_PATROL_RADIUS = 56;
export const NECROMANCER_IDLE_DURATION_MS = 5000;
export const NECROMANCER_PATROL_PAUSE_MS = 4000;
export const NECROMANCER_RETREAT_SPEED = 100;
export const NECROMANCER_SUMMON_COOLDOWN_MS = 12000;
export const NECROMANCER_SUMMON_COUNT = 2;
export const NECROMANCER_SUMMON_DURATION_MS = 1600;
/** Berserker defaults. */
export const BERSERKER_HEALTH = 550;
export const BERSERKER_SPEED = 80;
export const BERSERKER_CHASE_SPEED = 110;
export const BERSERKER_AGGRO_RANGE = 180;
export const BERSERKER_DEAGGRO_RANGE = 260;
export const BERSERKER_PATROL_RADIUS = 96;
export const BERSERKER_IDLE_DURATION_MS = 3600;
export const BERSERKER_PATROL_PAUSE_MS = 2400;
export const BERSERKER_ENRAGE_THRESHOLD = 0.4;
export const BERSERKER_ENRAGE_SPEED_MULT = 1.5;
// ── Realm Boss constants (spec §§6–9) ──
/** Hollow Warden — Verdana floor 5. */
export const HOLLOW_WARDEN_HEALTH = 800;
export const HOLLOW_WARDEN_SPEED = 60;
export const HOLLOW_WARDEN_CHASE_SPEED = 90;
export const HOLLOW_WARDEN_AGGRO_RANGE = 400;
export const HOLLOW_WARDEN_DEAGGRO_RANGE = 500;
export const HOLLOW_WARDEN_PATROL_RADIUS = 128;
export const HOLLOW_WARDEN_IDLE_DURATION_MS = 2000;
export const HOLLOW_WARDEN_PATROL_PAUSE_MS = 1600;
export const HOLLOW_WARDEN_SHIELD_ARC = Math.PI; // 180° frontal
export const HOLLOW_WARDEN_SUMMON_COOLDOWN_MS = 36000;
export const HOLLOW_WARDEN_SUMMON_COOLDOWN_P2_MS = 24000;
export const HOLLOW_WARDEN_SUMMON_COUNT = 2;
/** Duneshade — Sablewind floor 7. */
export const DUNESHADE_HEALTH = 650;
export const DUNESHADE_SPEED = 60;
export const DUNESHADE_CHASE_SPEED = 180;
export const DUNESHADE_AGGRO_RANGE = 400;
export const DUNESHADE_DEAGGRO_RANGE = 500;
export const DUNESHADE_PATROL_RADIUS = 128;
export const DUNESHADE_IDLE_DURATION_MS = 2000;
export const DUNESHADE_PATROL_PAUSE_MS = 1600;
export const DUNESHADE_DASH_RANGE = 256;
export const DUNESHADE_STEALTH_SPEED = 60;
export const DUNESHADE_STEALTH_ENTER_MS = 2400;
export const DUNESHADE_REVEAL_RANGE = 64;
export const DUNESHADE_ZONE_RADIUS = 128;
export const DUNESHADE_ZONE_DURATION_MS = 8000;
export const DUNESHADE_ZONE_COOLDOWN_MS = 24000;
export const DUNESHADE_MAX_ZONES = 2;
/** Frostbound Matriarch — Frostmere floor 10. */
export const FROSTBOUND_HEALTH = 700;
export const FROSTBOUND_SPEED = 55;
export const FROSTBOUND_CHASE_SPEED = 70;
export const FROSTBOUND_AGGRO_RANGE = 400;
export const FROSTBOUND_DEAGGRO_RANGE = 500;
export const FROSTBOUND_PATROL_RADIUS = 128;
export const FROSTBOUND_IDLE_DURATION_MS = 2000;
export const FROSTBOUND_PATROL_PAUSE_MS = 1600;
export const FROSTBOUND_TELEPORT_RANGE = 300;
export const FROSTBOUND_TELEPORT_TRIGGER = 150;
export const FROSTBOUND_TELEPORT_COOLDOWN_MS = 16000;
export const FROSTBOUND_TELEPORT_COOLDOWN_P2_MS = 10000;
export const FROSTBOUND_SLOW_MULTIPLIER = 0.6;
export const FROSTBOUND_SLOW_DURATION_MS = 4000;
export const FROSTBOUND_SUMMON_COOLDOWN_MS = 32000;
export const FROSTBOUND_SUMMON_COUNT = 3;
/** Emberborn Juggernaut — Ember Mire floor 10. */
export const EMBERBORN_HEALTH = 950;
export const EMBERBORN_SPEED = 80;
export const EMBERBORN_CHASE_SPEED = 110;
export const EMBERBORN_AGGRO_RANGE = 400;
export const EMBERBORN_DEAGGRO_RANGE = 500;
export const EMBERBORN_PATROL_RADIUS = 128;
export const EMBERBORN_IDLE_DURATION_MS = 2000;
export const EMBERBORN_PATROL_PAUSE_MS = 1600;
export const EMBERBORN_ENRAGE_THRESHOLD = 0.5;
export const EMBERBORN_ENRAGE_SPEED_MULT = 1.35;
export const EMBERBORN_WRAITH_COUNT = 4;
export const EMBERBORN_HP_PER_WRAITH_KILLED = 50;
// ── Overworld constants ──
/**
 * Overworld map width in tiles.
 * Expanded 2026-04-25 from 180×134 to 850×700 for chunked rendering.
 * Previous expansion (2026-04-17): 80×60 → 180×134 for 4-realm layout.
 * See `docs/design/realms-and-bosses.md §1.3`.
 */
export const OVERWORLD_WIDTH = 850;
/** Overworld map height in tiles */
export const OVERWORLD_HEIGHT = 700;
/**
 * Pre-chunking overworld footprint (kept for reference + tests that still
 * want to smoke-test the small-map code path). The only caller at time of
 * the expansion was `client/src/scenes/OverworldScene.ts`, which now sources
 * the live dimensions from `TilemapPayload` at runtime — these constants
 * are a fallback for offline/asset-gen tooling.
 */
export const LEGACY_OVERWORLD_WIDTH = 180;
export const LEGACY_OVERWORLD_HEIGHT = 134;
// ── Overworld crop (AGE-1318) ──
/** Temporary overworld tile limit for faster iteration.
 *  When > 0, the server crops the overworld map to this many tiles per axis,
 *  centered on the default spawn. Set to 0 to disable cropping. */
export const OVERWORLD_TILE_LIMIT = 500;
// ── Elevation constants (AGE-1129) ──
/** Minimum tile elevation value */
export const ELEVATION_MIN = 0;
/** Maximum tile elevation value */
export const ELEVATION_MAX = 8;
/** Elevation difference >= this between adjacent tiles = impassable cliff.
 * 2 means only single-step elevation changes (diff=1) are walkable ramps;
 * anything steeper is an impassable cliff. */
export const ELEVATION_CLIFF_THRESHOLD = 2;
/** Movement cost multiplier when walking uphill (elevation diff > 0) */
export const ELEVATION_UPHILL_COST = 1.5;
/** Movement cost multiplier when walking downhill (elevation diff < 0) */
export const ELEVATION_DOWNHILL_COST = 0.8;
// ── Chunk constants (AGE-937) ──
/** Chunk size in tiles — each chunk is CHUNK_SIZE×CHUNK_SIZE tiles */
export const CHUNK_SIZE = 32;
/** Number of chunks around the viewport to keep loaded (buffer ring) */
export const CHUNK_LOAD_RADIUS = 1;
/** Distance in chunks before triggering a new chunk request to the server */
export const CHUNK_REQUEST_THRESHOLD = 1;
// ── AOI (Area of Interest) constants ──
/**
 * Radius in world-pixels around each player within which entities are
 * synced to that client.  Roughly 20 tiles (~640px at TILE_SIZE=32).
 * Chosen to comfortably exceed the client viewport (800×600 nominal) so
 * entities are already present before they scroll into view.
 */
export const AOI_RADIUS_PX = 640;
/**
 * Extra buffer added to the AOI radius when *removing* an entity from a
 * client's visible set.  Without a buffer, entities near the boundary
 * would flicker in/out as the player oscillates across the edge.  The
 * effective "leave" radius is `AOI_RADIUS_PX + AOI_HYSTERESIS_PX`.
 */
export const AOI_HYSTERESIS_PX = 128;
/**
 * Cell size for the server-side SpatialGrid.  Entities are bucketed into
 * square cells of this width.  A larger cell means fewer cell lookups per
 * query but more false positives; 512px (~16 tiles) is a reasonable
 * default for the expanded overworld.
 */
export const AOI_CELL_SIZE_PX = 512;
// ── Game flow config constants ──
/** Minimum players required to start a game */
export const MIN_PLAYERS_TO_START = 1;
/** Countdown (ms) after all players ready before loading phase */
export const LOBBY_COUNTDOWN_MS = 3000;
/** Duration of loading phase (ms) — minimum time to show loading screen */
export const LOADING_PHASE_MS = 2000;
/** Duration of results phase before returning to lobby (ms) */
export const RESULTS_PHASE_MS = 10000;
/** Extraction zone interaction range in pixels (cancel if player leaves) */
export const EXTRACTION_CANCEL_RANGE = 80;
/** Maximum players per room */
export const MAX_ROOM_PLAYERS = 4;
// ── Tutorial overlay tuning knobs ──
/** Minimum time (ms) a tutorial prompt stays visible before advancing */
export const TUTORIAL_MIN_DISPLAY_MS = 1500;
/** Cooldown (ms) between consecutive tutorial steps */
export const TUTORIAL_COOLDOWN_MS = 2000;
/** Fade-in duration (ms) for tutorial prompts */
export const TUTORIAL_FADE_IN_MS = 200;
/** Fade-out duration (ms) for tutorial prompts */
export const TUTORIAL_FADE_OUT_MS = 150;
/** Depth layer for tutorial overlay elements */
export const TUTORIAL_DEPTH = 1800;
/** Background opacity for tutorial prompt box (0–1) */
export const TUTORIAL_BG_ALPHA = 0.85;
/** Window (ms) for double-Escape skip detection */
export const TUTORIAL_SKIP_WINDOW_MS = 1000;
/** Time (ms) after first kill to trigger loot fallback text */
export const TUTORIAL_LOOT_FALLBACK_MS = 45000;
/** Boss floor number — suppress tutorial prompts on this floor */
export const TUTORIAL_BOSS_FLOOR = 5;
// ── Guest Account System ──
/** Maximum length for player display names */
export const GUEST_NAME_MAX_LENGTH = 20;
/** Minimum length for player display names */
export const GUEST_NAME_MIN_LENGTH = 1;
/** Default name prefix for auto-generated guest names */
export const GUEST_NAME_PREFIX = 'Adventurer';
/** Session token expiry in milliseconds (7 days) */
export const GUEST_SESSION_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;
// ── Boulder puzzle (Round 17 Phase D) ──
/** Boulder sprite size in pixels (2×2 tiles). */
export const BOULDER_SIZE_PX = 64;
/** Half of BOULDER_SIZE_PX — convenience for hit-box math. */
export const BOULDER_HALF_SIZE_PX = 32;
//# sourceMappingURL=gameConfig.js.map